import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { SocietyService } from '@/services/societyService';
import { QUERY_KEYS } from '@/constants';
import { useAuthStore } from '@/store/authStore';
import { useInfiniteList } from '@/hooks/useInfiniteList';
import type { ListParams, SocietyRegisterPayload } from '@/types/api';

export function useSocieties(params?: ListParams) {
  return useInfiniteList({
    queryKey: [...QUERY_KEYS.societies, { ...params, page: undefined }],
    queryFn: (page) => SocietyService.list({ ...params, page }),
    pageSize: params?.limit,
  });
}

export function useSociety(id?: string) {
  return useQuery({
    queryKey: QUERY_KEYS.society(id || ''),
    queryFn: async () => {
      const res = await SocietyService.getById(id!);
      return res.data;
    },
    enabled: Boolean(id),
  });
}

export function useRegisterSociety() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: SocietyRegisterPayload) => SocietyService.register(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.societies });
    },
  });
}

export function useUpdateSociety(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<SocietyRegisterPayload>) =>
      SocietyService.update(id, payload),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.society(id) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.societies });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.licenseStatus });
      const updated = res.data;
      if (updated) {
        const { tenant, setTenant } = useAuthStore.getState();
        if (tenant && (tenant.id === id || tenant._id === id)) {
          setTenant({
            ...tenant,
            ...(updated.name ? { name: updated.name } : {}),
            ...(updated.registrationNumber
              ? { registrationNumber: updated.registrationNumber }
              : {}),
            ...(updated.societyType ? { societyType: updated.societyType } : {}),
            ...(updated.licensedHouseCount !== undefined
              ? { licensedHouseCount: updated.licensedHouseCount }
              : {}),
          });
        }
      }
    },
  });
}

export function useUploadSocietyLogo(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => SocietyService.uploadLogo(id, file),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.society(id) });
    },
  });
}
